import { useEffect, useState } from 'react'
import * as api from 'api/entityApi'
import type { Option } from 'types/entity'
import { toLegacyDateTime } from './ContactCheckboxes'

interface AppointmentFormProps {
  entityId: string
  onSuccess?: (msg: string) => void
  onCancel?: () => void
}

/** Port of the legacy Add Appointment (InsertNewTaskOrAppointment, IsAppointment). */
export function AppointmentForm({ entityId, onSuccess }: AppointmentFormProps) {
  const [users, setUsers] = useState<Option[]>([])

  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [location, setLocation] = useState<string>('')
  const [completeAddress, setCompleteAddress] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [assignedTo, setAssignedTo] = useState<string>('')
  const [collaborators, setCollaborators] = useState<string[]>([])
  const [remarks, setRemarks] = useState<string>('')

  const [notifyBySms, setNotifyBySms] = useState<boolean>(false)
  const [notifyByEmail, setNotifyByEmail] = useState<boolean>(false)
  const [dltRegistered, setDltRegistered] = useState<boolean>(true)
  const [dltMessage, setDltMessage] = useState<string>('')

  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    let active = true
    async function loadData() {
      try {
        const [uList, dlt] = await Promise.all([
          api.fetchAssignedUsers(),
          api.fetchDltTemplateStatus('task reminder').catch(() => ({
            isRegistered: false,
            dltTemplateId: '',
            message: 'DLT registration status could not be checked.',
          })),
        ])
        if (!active) return
        setUsers(uList)
        if (uList.length > 0) setAssignedTo(uList[0].id)
        setDltRegistered(dlt.isRegistered)
        setDltMessage(dlt.message)
      } catch {
        if (active) setError('Failed to load user list.')
      } finally {
        if (active) setLoading(false)
      }
    }
    void loadData()
    return () => {
      active = false
    }
  }, [])

  const handleReset = () => {
    setTitle('')
    setDescription('')
    setLocation('')
    setCompleteAddress('')
    setStartDate('')
    setEndDate('')
    if (users.length > 0) setAssignedTo(users[0].id)
    setCollaborators([])
    setRemarks('')
    setNotifyBySms(false)
    setNotifyByEmail(false)
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      setError('Appointment title is required.')
      return
    }
    if (!startDate) {
      setError('Start date is required.')
      return
    }
    if (!endDate) {
      setError('End date is required.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const msg = await api.submitActionAppointment({
        entityId,
        title,
        description,
        location,
        completeAddress,
        startDate: toLegacyDateTime(startDate),
        endDate: toLegacyDateTime(endDate),
        assignedTo,
        collaboratorIds: collaborators.join(','),
        remarks,
        notifyBySms,
        notifyByEmail,
      })
      onSuccess?.(msg)
    } catch (err) {
      setError((err as Error).message || 'Failed to schedule appointment.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="widget-loader">Loading Appointment settings…</div>

  return (
    <div className="action-form">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="ap-title">
          Title <span className="req">*</span>
        </label>
        <input
          id="ap-title"
          type="text"
          className="form-control"
          placeholder="Appointment title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="ap-description">
          Description
        </label>
        <textarea
          id="ap-description"
          rows={2}
          className="form-control"
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="ap-location">
          Location
        </label>
        <input
          id="ap-location"
          type="text"
          className="form-control"
          placeholder="Location / online link"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="ap-address">
          Complete Address
        </label>
        <textarea
          id="ap-address"
          rows={2}
          className="form-control"
          placeholder="Complete address"
          value={completeAddress}
          onChange={(e) => setCompleteAddress(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="ap-start">
          Start Date &amp; Time <span className="req">*</span>
        </label>
        <input
          id="ap-start"
          type="datetime-local"
          className="form-control"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="ap-end">
          End Date &amp; Time <span className="req">*</span>
        </label>
        <input
          id="ap-end"
          type="datetime-local"
          className="form-control"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="ap-assigned">
          Assigned To
        </label>
        <select
          id="ap-assigned"
          className="form-control"
          value={assignedTo}
          onChange={(e) => setAssignedTo(e.target.value)}
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="ap-collab">
          Collaborators
        </label>
        <select
          id="ap-collab"
          className="form-control"
          multiple
          size={Math.min(4, Math.max(2, users.length))}
          value={collaborators}
          onChange={(e) =>
            setCollaborators(Array.from(e.target.selectedOptions, (option) => option.value))
          }
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="ap-remarks">
          Remarks
        </label>
        <textarea
          id="ap-remarks"
          rows={2}
          className="form-control"
          placeholder="Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </div>

      <div className="form-group flex-row">
        <label
          className="checkbox-label"
          htmlFor="ap-notify-sms"
          title={!dltRegistered ? dltMessage : undefined}
        >
          <input
            id="ap-notify-sms"
            type="checkbox"
            disabled={!dltRegistered}
            checked={notifyBySms}
            onChange={(e) => setNotifyBySms(e.target.checked)}
          />
          <span>Notify by SMS</span>
        </label>
        <label className="checkbox-label" htmlFor="ap-notify-email">
          <input
            id="ap-notify-email"
            type="checkbox"
            checked={notifyByEmail}
            onChange={(e) => setNotifyByEmail(e.target.checked)}
          />
          <span>Notify by Email</span>
        </label>
      </div>
      {!dltRegistered && dltMessage && <div className="form-hint">{dltMessage}</div>}

      <div className="action-form-footer">
        <button type="button" className="btn btn-default" onClick={handleReset} disabled={submitting}>
          Reset
        </button>
        <button type="button" className="btn pri-btn" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Scheduling…' : 'Add Appointment'}
        </button>
      </div>
    </div>
  )
}
